// **ブラウザのノイズ抑制だけの効きを測る**（タスク #26）。**診断専用**（本番では外す。DOC-0026 §1）。
//
// なぜ要るか:
//   自前のノイズ抑制（`client/core` の nnnoiseless）の効きは DOC-0023 §5-2 で「実質 6〜11 dB」と
//   測ってあるが、**比較相手が無い**。通話では `getUserMedia` の `noiseSuppression` が常に
//   入っている（`audio.ts`。2026-09-17 から変わっていない）ので、**自前の抑制はブラウザが
//   削り残したものだけを相手にしている**。
//
//   2026-10-08 の試験で自前の抑制を 77 秒切ったが、上りのビットレートは増えなかった
//   （22.2 → 20.4 kbps。1 パケット平均 46.5 → 36.8 B・満載 60 B）。騒音が符号化器に届いていれば
//   可変ビットレートが反応し無送信（DTX）も止まるので、**騒音は自前の抑制より手前で既に
//   消えていた**。ただし消したのがブラウザか、マイクの位置か、その時の騒音の小ささかは
//   切り分けられていない（2026-10-01 は同じブラウザ設定で「ファン音が大きく声が埋もれる」だった）。
//
// どう測るか:
//   同じ場所・同じマイク・同じ騒音で 3 通りの条件で録る。**通話はしない**。
//     A: echoCancellation=true  / noiseSuppression=true   （アプリと同じ）
//     B: echoCancellation=true  / noiseSuppression=false  （抑制の指定だけ外す）
//     C: echoCancellation=false / noiseSuppression=false  （音声処理を全部外す）
//   B − A が指定だけの効き、C − B がそれ以外の処理、**C − A がブラウザぜんぶの効き**。
//   **Safari は音声処理をまとめて掛ける**ので、C でないと素のマイクにならないことがある。

import workletUrl from "./ns-recorder-worklet.ts?worker&url";
import { rmsDb, toWav } from "./wav.ts";

/** 1 条件あたりの長さ（秒）。前半が騒音だけ、後半が声 + 騒音。 */
export const SECONDS = 10;
/** 黙る区間の長さ（秒）。 */
export const QUIET_SECONDS = 5;
/** プラットフォーム層と同じ標本化周波数（`audio.ts` の `SAMPLE_RATE_HZ`）。 */
const SAMPLE_RATE_HZ = 48_000;

export interface Pass {
  key: "A" | "B" | "C";
  label: string;
  echoCancellation: boolean;
  noiseSuppression: boolean;
}

/**
 * 測る 3 条件。**順番に意味がある**（A でマイクを決め、B・C は同じマイクに固定する）。
 *
 * `autoGainControl` は 3 条件とも false。入れると音量が勝手に動いてレベルの比較ができない
 * （アプリも false）。
 */
export const PASSES: Pass[] = [
  { key: "A", label: "アプリと同じ（抑制あり）", echoCancellation: true, noiseSuppression: true },
  { key: "B", label: "ノイズ抑制だけ切る", echoCancellation: true, noiseSuppression: false },
  { key: "C", label: "音声処理を全部切る", echoCancellation: false, noiseSuppression: false },
];

export interface PassResult {
  pass: Pass;
  /** 前半（騒音だけ）の 2 乗平均（dBFS）。**ここが小さいほど騒音が削れている**。 */
  quietDb: number;
  /** 後半（声 + 騒音）の 2 乗平均（dBFS）。 */
  voicedDb: number;
  /** ファイル全体の 2 乗平均（dBFS）。 */
  overallDb: number;
  /** 実際に録れた長さ（秒）。取りこぼしがあれば `SECONDS` より短くなる。 */
  seconds: number;
  sampleRateHz: number;
  /** `track.getSettings()` が答えた値。**要求が通ったかの確認用**。 */
  applied: { echoCancellation?: boolean; noiseSuppression?: boolean };
  /** マイクの名前（`label`）。空の端末もある。 */
  micLabel: string;
  /** 実際に使われたマイクの `deviceId`。2 回目以降をこれに固定する。取れない端末もある。 */
  deviceId: string | null;
  wav: Blob;
}

/** 条件をまたいだ差。**知りたいのは `browserTotal`**。 */
export interface ProbeDiff {
  /** B − A（前半）。`noiseSuppression` の指定だけの効き。 */
  suppressionOnly: number | null;
  /** C − B（前半）。それ以外の音声処理の効き。 */
  otherProcessing: number | null;
  /** C − A（前半）。**ブラウザの音声処理ぜんぶの効き**。 */
  browserTotal: number | null;
}

/** 差を出す。条件が欠けていれば `null`（途中で失敗しても読めるようにする）。 */
export function diffOf(results: PassResult[]): ProbeDiff {
  const q = (key: Pass["key"]) => results.find((r) => r.pass.key === key)?.quietDb ?? null;
  const [a, b, c] = [q("A"), q("B"), q("C")];
  const sub = (x: number | null, y: number | null) => (x === null || y === null ? null : x - y);
  return {
    suppressionOnly: sub(b, a),
    otherProcessing: sub(c, b),
    browserTotal: sub(c, a),
  };
}

/** 結果 1 件をログの 1 行にする。**純粋関数**なので試験できる。 */
export function formatPassResult(r: PassResult): string {
  const applied = (key: "echoCancellation" | "noiseSuppression", want: boolean) => {
    const actual = r.applied[key];
    if (actual === undefined) return `${key}=不明`;
    return `${key}=${actual}${actual === want ? "" : `（要求 ${want} が通っていない）`}`;
  };
  return (
    `ブラウザ抑制 ${r.pass.key}（${r.pass.label}）: ` +
    `前半 ${r.quietDb.toFixed(1)} dBFS / 後半 ${r.voicedDb.toFixed(1)} dBFS / ` +
    `差 ${(r.voicedDb - r.quietDb).toFixed(1)} dB / 全体 ${r.overallDb.toFixed(1)} dBFS / ` +
    `${r.seconds.toFixed(1)} 秒 ${r.sampleRateHz} Hz / ` +
    `${applied("echoCancellation", r.pass.echoCancellation)} / ` +
    `${applied("noiseSuppression", r.pass.noiseSuppression)} / マイク「${r.micLabel || "名前なし"}」`
  );
}

/** 差をログの行にする。 */
export function formatDiff(d: ProbeDiff): string[] {
  const f = (v: number | null) => (v === null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)} dB`);
  return [
    `ブラウザ抑制: ノイズ抑制の指定だけの効き（B − A・前半） ${f(d.suppressionOnly)}`,
    `ブラウザ抑制: それ以外の音声処理の効き（C − B・前半） ${f(d.otherProcessing)}`,
    `ブラウザ抑制: **ブラウザぜんぶの効き（C − A・前半） ${f(d.browserTotal)}**`,
  ];
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 1 条件ぶん録る。
 *
 * **`AudioContext` は条件ごとに作って閉じる。** Safari は音声処理をマイクの取得時に
 * まとめて決めるので、使い回すと B・C が A と同じ音になりうる。
 */
async function recordOne(
  pass: Pass,
  deviceId: string | null,
  onTick: (secondsDone: number) => void,
): Promise<PassResult> {
  const audio: MediaTrackConstraints = {
    channelCount: 1,
    sampleRate: SAMPLE_RATE_HZ,
    echoCancellation: pass.echoCancellation,
    noiseSuppression: pass.noiseSuppression,
    autoGainControl: false,
  };
  if (deviceId) audio.deviceId = { exact: deviceId };

  const stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
  const track = stream.getAudioTracks()[0];
  if (!track) throw new Error("音声トラックが取れない");
  const settings = track.getSettings();

  let context: AudioContext;
  try {
    context = new AudioContext({ sampleRate: SAMPLE_RATE_HZ, latencyHint: "interactive" });
  } catch {
    context = new AudioContext();
  }

  const chunks: Float32Array[] = [];
  // **閉じたあとに `context.sampleRate` を読めない**ので、閉じる前に控える
  let sampleRateHz = SAMPLE_RATE_HZ;
  try {
    await context.audioWorklet.addModule(workletUrl);
    await context.resume();
    const node = new AudioWorkletNode(context, "ns-recorder", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    node.port.onmessage = (e: MessageEvent<Float32Array>) => chunks.push(e.data);

    // **出力まで繋がないと処理が回らない実装がある。** 音は出したくないので 0 倍を挟む
    // （ここでマイクの音を鳴らすと、条件 C（エコーキャンセル切）で回り込む）。
    const mute = context.createGain();
    mute.gain.value = 0;
    node.connect(mute);
    mute.connect(context.destination);

    const source = context.createMediaStreamSource(stream);
    source.connect(node);

    for (let s = 0; s < SECONDS; s++) {
      onTick(s);
      await sleep(1000);
    }

    source.disconnect();
    node.disconnect();
    mute.disconnect();
  } finally {
    track.stop();
    sampleRateHz = context.sampleRate;
    await context.close();
  }

  let total = 0;
  for (const c of chunks) total += c.length;
  const samples = new Float32Array(total);
  let at = 0;
  for (const c of chunks) {
    samples.set(c, at);
    at += c.length;
  }

  // **区間で分ける**（発話検出で分けると抑制の強さで判定位置が動き、条件間の比較にならない）
  const split = Math.min(samples.length, Math.round(QUIET_SECONDS * sampleRateHz));
  return {
    pass,
    quietDb: rmsDb(samples, 0, split),
    voicedDb: rmsDb(samples, split, samples.length),
    overallDb: rmsDb(samples, 0, samples.length),
    seconds: sampleRateHz > 0 ? samples.length / sampleRateHz : 0,
    sampleRateHz,
    applied: {
      echoCancellation: settings.echoCancellation,
      noiseSuppression: settings.noiseSuppression,
    },
    micLabel: track.label,
    deviceId: settings.deviceId ?? null,
    wav: toWav(samples, sampleRateHz),
  };
}

export interface RunOptions {
  /** 「黙る」「話す」の表示。 */
  onPhase: (text: string) => void;
  /** 1 条件が終わるたびに呼ぶ（結果を順に画面へ出すため）。 */
  onResult: (result: PassResult) => void;
}

/**
 * 3 条件を続けて測る。**2 回目以降は 1 回目と同じマイクに固定**する
 * （途中で別のマイクに切り替わると比較にならない）。
 */
export async function runBrowserNsProbe(options: RunOptions): Promise<PassResult[]> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("このブラウザは getUserMedia を持っていない");
  }
  const results: PassResult[] = [];
  let deviceId: string | null = null;
  for (const pass of PASSES) {
    const result = await recordOne(pass, deviceId, (done) => {
      const quiet = done < QUIET_SECONDS;
      options.onPhase(
        `${quiet ? "黙る" : "話す"} — ${pass.key}（${pass.label}）あと ${SECONDS - done} 秒`,
      );
    });
    if (!deviceId) deviceId = result.deviceId;
    results.push(result);
    options.onResult(result);
    if (pass !== PASSES[PASSES.length - 1]) {
      // マイクを完全に手放してから次へ（Safari が前の条件の処理を引き継がないように）
      options.onPhase("待つ — 次の条件に移ります");
      await sleep(1500);
    }
  }
  return results;
}
